<template>
    <section class="pl-showcase">
        <!-- The campaigns' own banners, one plate each, crossfaded. They are
             rendered up front rather than swapped into a single element so a
             hover never waits on a network fetch: the picture is already
             decoded and only its opacity changes. -->
        <div class="pl-showcase__plates" aria-hidden="true">
            <div
                v-for="campaign in platedCampaigns"
                :key="campaign.id"
                class="pl-showcase__plate"
                :class="{ 'is-on': campaign.id === previewId }"
                :style="bannerStyle(campaign)"
            ></div>
            <div class="pl-showcase__veil" :class="{ 'is-lit': !!preview }"></div>
        </div>

        <div class="pl-showcase__copy">
            <template v-if="preview">
                <p class="pl-showcase__eyebrow">{{ t('player.lobby.campaignEyebrow') }}</p>
                <h1 class="pl-showcase__title">{{ preview.name }}</h1>
                <p class="pl-showcase__lede">
                    {{ preview.description || t('player.lobby.campaignNoDescription') }}
                </p>
                <p class="pl-showcase__meta">
                    <span class="pl-chip">
                        {{ t('player.lobby.charactersLabel') }}<em>{{ preview.characterCount }}</em>
                    </span>
                    <span v-if="preview.is_completed" class="pl-chip pl-chip--muted">
                        {{ t('player.lobby.campaignCompleted') }}
                    </span>
                    <span v-if="preview.id === activeId" class="pl-chip pl-chip--muted">
                        {{ t('player.lobby.campaignCurrent') }}
                    </span>
                </p>
                <button
                    type="button"
                    class="pl-btn pl-btn--primary pl-showcase__cta"
                    @click="$emit('open', preview.id)"
                >
                    {{ t('player.lobby.openCampaign', { name: preview.name }) }}
                </button>
            </template>

            <template v-else>
                <h1 ref="idleTitle" tabindex="-1" class="pl-showcase__title">
                    {{ t('player.lobby.chooseCampaignTitle') }}
                </h1>
                <p class="pl-showcase__lede">{{ t('player.lobby.showcaseHint') }}</p>
                <button
                    type="button"
                    class="pl-btn pl-btn--ghost pl-btn--sm pl-showcase__cta"
                    @click="$emit('refresh')"
                >
                    <i class="fas fa-rotate" :class="{ 'pl-spinner': loading }" aria-hidden="true"></i>
                    {{ t('player.common.refresh') }}
                </button>
            </template>
        </div>

        <LobbyStrip
            :items="campaigns"
            :selected-id="previewId"
            :marked-id="activeId"
            :label="openLabel"
            :aria-label="t('player.lobby.stripLabel')"
            :loading="loading && !campaigns.length"
            @hover="previewId = $event"
            @select="$emit('open', $event)"
        >
            <template #default="{ item }">
                <span class="pl-mini__art" :style="bannerStyle(item)" aria-hidden="true"></span>
                <span class="pl-mini__name">{{ item.name }}</span>
            </template>
        </LobbyStrip>
    </section>
</template>

<script setup>
import {computed, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import LobbyStrip from '@/components/Player/LobbyStrip.vue';
import {bannerStyle, hasBanner} from '@/components/Player/campaignBanner.js';

const props = defineProps({
    campaigns: {type: Array, default: () => []},
    activeId: {type: String, default: null},
    loading: {type: Boolean, default: false},
});

defineEmits(['open', 'refresh']);

const {t} = useI18n();

const previewId = ref(null);
const idleTitle = ref(null);

const preview = computed(
    () => props.campaigns.find((campaign) => campaign.id === previewId.value) ?? null,
);

const platedCampaigns = computed(() => props.campaigns.filter(hasBanner));

function openLabel(campaign) {
    return t('player.lobby.openCampaign', {name: campaign.name});
}

defineExpose({focus: () => idleTitle.value?.focus()});
</script>

<style scoped>
</style>
